/* eslint-disable @next/next/no-html-link-for-pages */
import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/nextjs";
import axios from "axios";
import _ from "lodash";
import { signIn, useSession } from "next-auth/react";
import Head from "next/head";
import { useRouter } from "next/router";
import { GetServerSideProps } from "next/types";
import { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";
import { CountryNavbarComponent } from "../components/Auth/CountryDropdown/CountryDropdown";
import { AuthOtpComponent } from "../components/Auth/Otp/Otp";
import {
  OtpFormSchema,
  OtpFormSchemaType,
} from "../components/Auth/Otp/OtpFormSchema";
import { AuthSignupComponent } from "../components/Auth/Signup/Signup";
import {
  SignupFormSchema,
  SignupFormSchemaType,
} from "../components/Auth/Signup/SignupFormSchema";
import AuthLayout from "../components/Layouts/Auth/Auth";
import { useFlag } from "../context/featureFlags/featureFlagsContext";
import { authGetOtpController } from "../lib/controllers/auth/otp/get";
import getCampaign from "../lib/controllers/campaign/getCampaign";
import { giveaway0123CampaignData } from "../lib/data/campaign/giveaway0123";
import { renew2022CampaignData } from "../lib/data/campaign/renew2022";
import { featureFlag } from "../lib/featureFlags/featureFlags";
import { getIpCountry } from "../lib/helper/getIpCountry";
import { useSegmentAnalytics } from "../lib/hooks/useSegmentAnalytics";
import useSessionStorage from "../lib/hooks/useSessionStorage";
import { LegalAcknowledgementType } from "../modules/legal/models";
import tenantConfig from "../tenant.config";
import { PrimeLink } from "../components/prime/link";

export const getServerSideProps: GetServerSideProps = async ({
  query,
  req,
}) => {
  const { campaign, callbackUrl } = query;

  const defaultCountry = getIpCountry(req);

  // if theres country code, return them as props
  // Discord token giveaway campaign - /signup?campaign=giveaway0123
  if (
    typeof campaign === "string" &&
    campaign.toLowerCase() === giveaway0123CampaignData.signup.campaignCode
  ) {
    if (featureFlag("discordGiveaway0323")) {
      return {
        props: {
          campaignCode: campaign.toLowerCase(),
          title: giveaway0123CampaignData.signup?.title,
          info: giveaway0123CampaignData.signup?.info,
          countryCode: defaultCountry || null,
        },
      };
    } else {
      return {
        redirect: {
          permanent: false,
          destination: "/signup",
        },
      };
    }
  }

  if (campaign) {
    // If there's campaign code grab relevant campaign data and return them
    const campaignData = getCampaign(campaign as string);
    if (campaignData) {
      return {
        props: {
          campaignCode: campaignData.data.code,
          title: campaignData.login?.title,
          info: campaignData.login?.info,
          callbackUrl: callbackUrl || "",
          countryCode: defaultCountry || null,
        },
      };
    } else {
      return {
        redirect: {
          permanent: false,
          destination: "/signup",
        },
      };
    }
  }
  return {
    props: {
      callbackUrl: callbackUrl || null,
      countryCode: defaultCountry || null,
    },
  };
};

/**************************************************
 *
 * View starts here
 *
 **************************************************/

const IS_RECAPTCHA_ENABLED = featureFlag("recaptcha")

function SignUpPage(props: {
  // For redirecting user back to previous page after login
  callbackUrl?: string;

  // For displaying campaign-related data
  campaignCode?: string;
  title?: string;
  info?: string;
  //country specific auth
  countryCode?: string;
}) {
  const [isFetching, setIsFetching] = useState(false);
  const [isSignupSuccess, setIsSignupSuccess] = useState<boolean>(false);
  const [shouldShowOtpForm, setShouldShowOtpForm] = useState(false);
  const [signupFormData, setSignupFormData] = useState<SignupFormSchemaType>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    ...(featureFlag("marketingOptIn") && { marketingPreferences: false }),
  });
  const router = useRouter();
  const { info, callbackUrl } = props;
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const useSignupFormProps = useForm<SignupFormSchemaType>({
    resolver: zodResolver(SignupFormSchema),
  });
  const useOtpFormProps = useForm<OtpFormSchemaType>({
    resolver: zodResolver(OtpFormSchema),
  });
  const session = useSession();
  const { segmentAnalytics } = useSegmentAnalytics();
  const isDiscordGiveaway0123 = useFlag("FEATURE_FLAG_DISCORD_GIVEAWAY_0123");
  //current country used to determine user selected country

  const { setItem, getItem } = useSessionStorage();
  const [isLoading, setLoading] = useState<boolean>(true);
  const [currentCountry, setCurrentCountry] = useState<string>("");
  const [tncs, setTncs] = useState<any>([]);

  useEffect(() => {
    let types: string[] = [];
    if (!currentCountry) return;
    switch (currentCountry) {
      case "MY":
        types = [
          LegalAcknowledgementType.MY_TERMS_OF_USE,
          LegalAcknowledgementType.MY_PRIVACY_POLICY,
          LegalAcknowledgementType.MY_RISK_WARNING_DISCLAIMERS,
        ];
        break;
      default:
        types = [
          LegalAcknowledgementType.SG_TERMS_OF_USE,
          LegalAcknowledgementType.SG_PRIVACY_POLICY,
          LegalAcknowledgementType.SG_RISK_DISCLOSURES,
        ];
        break;
    }
    axios
      .get("/api/legal/getTnC", {
        params: {
          types: types.join(","),
        },
      })
      .then((res) => {
        setTncs(res.data);
      });
  }, [currentCountry]);

  useEffect(() => {
    const sessionCountry = getItem("selectedCountry");
    if (sessionCountry) {
      setCurrentCountry(sessionCountry);
    } else {
      setCurrentCountry(props.countryCode === "MY" ? "MY" : "SG");
    }
    setLoading(false);
  }, [props.countryCode]);

  const setCountry = (selectedCountry: string) => {
    setCurrentCountry(selectedCountry);
    setShouldShowOtpForm(false);
    useSignupFormProps.reset();
    setItem("selectedCountry", selectedCountry);
  };

  useEffect(() => {
    if (
      session &&
      session.data &&
      session.data.user &&
      session.status === "authenticated"
    ) {
      // Identify user on Segment
      segmentAnalytics
        .identify(session.data.user.userId, {
          createdAt: new Date(),
          userId: session.data.user.userId,
          traderId: session.data.user.traderId,
          traderType: session.data.user.traderType,
          accreditationStatus: session.data.user.accreditationStatus,
          kycVerificationStatus: session.data.user.kycVerificationStatus,
        })
        .finally(() => {
          segmentAnalytics.track("Investor Signup");
        });

      // Set user context on Sentry
      !_.isNil(session.data.user.userEmail) &&
        Sentry.setUser({ email: session.data.user.userEmail });
      Sentry.setContext("User", session.data.user as unknown as { [key: string]: unknown });
    }
  }, [session]);

  const onSubmitSignup = async (data: SignupFormSchemaType) => {
    try {
      let token = "default";
      if (IS_RECAPTCHA_ENABLED) {
        // Get recaptcha token
        if (!recaptchaRef.current) throw "reCaptcha null";
        token = (await recaptchaRef.current?.executeAsync()) ?? "";
        if (!token) throw "token null";
      }

      // Request otp
      const [_, error] = await authGetOtpController(
        data.email,
        "signup",
        token,
        currentCountry,
        {
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          password: data.password,
          marketingPreferences: data.marketingPreferences,
        }
      );
      if (error && error.message !== "Not Found") {
        useSignupFormProps.setError(
          "email",
          { type: "custom", message: error.message + "" },
          { shouldFocus: true }
        );
        setIsFetching(false);
        recaptchaRef.current?.reset();
        return;
      }

      // Save info on localStorage to make the expiration time persistent
      // when the page is refreshed or the user tries to signup again in another tab
      const otpCountdownData = {
        email: data.email,
        otpSentAt: new Date().toJSON(),
        action: "signup",
        currentCountry,
        signupData: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          password: data.password,
          marketingPreferences: data.marketingPreferences,
        },
      };
      localStorage.setItem(
        "otpCountdownData",
        JSON.stringify(otpCountdownData)
      );

      // Show OTP form
      setSignupFormData({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        marketingPreferences: data.marketingPreferences,
      });
      setShouldShowOtpForm(true);
    } catch (error) {
      // Show error thrown from recaptcha token
      useSignupFormProps.setError(
        "email",
        { type: "custom", message: error + "" },
        { shouldFocus: true }
      );
      if (recaptchaRef.current) recaptchaRef.current.reset();
    } finally {
      setIsFetching(false);
    }
  };

  const onSubmitOtp = async (data: any) => {
    setIsFetching(true);

    // Process signup
    let payload: any = {
      email: signupFormData.email,
      firstName: signupFormData.firstName,
      lastName: signupFormData.lastName,
      password: signupFormData.password,
      marketingPreferences: signupFormData.marketingPreferences,
      otp: data.code,
      redirect: false,
      callbackUrl: decodeURIComponent(callbackUrl || "/terms-and-conditions"),
      signupCountry: featureFlag("ecf") ? currentCountry : "SG",
    };
    if (props.campaignCode && isDiscordGiveaway0123) {
      payload.signupCode = props.campaignCode;
    }
    const result = await signIn("signup", payload);

    // If signup process fail, show error to user
    if (result && result.ok === false) {
      const alreadyExistRegex = /(already exist)/;
      const errorMessage = result.error ?? "An error occurred"; // Use a default value if result.error is null
      if (alreadyExistRegex.test(result.error || "")) {
        setShouldShowOtpForm(false);
        useSignupFormProps.setError(
          "email",
          { type: "custom", message: errorMessage },
          { shouldFocus: true }
        );
      } else {
        useOtpFormProps.setError(
          "code",
          { type: "custom", message: errorMessage },
          { shouldFocus: true }
        );
      }
      setIsFetching(false);
      return;
    }

    // Redirect on successful signup
    setIsSignupSuccess(true);
    if (result && result.url) {
      localStorage.removeItem("otpCountdownData");
      sessionStorage.removeItem("selectedCountry");
      router.push(result.url!);
    }
  };

  // If diff between current time & localStorage time is < 5 mins show otp form
  useEffect(() => {
    const storedDataString = localStorage.getItem("otpCountdownData");
    if (storedDataString) {
      const storedData = JSON.parse(storedDataString);
      const signupData = storedData.signupData;
      const action = storedData.action;
      const otpSentAt = new Date(storedData.otpSentAt);
      const now = new Date();
      const timeDifference = Number(now) - Number(otpSentAt);
      const secondsDifference = Math.floor(timeDifference / 1000);

      if (action !== "signup") {
        localStorage.removeItem("otpCountdownData");
        return;
      }

      if (secondsDifference > 300) {
        localStorage.removeItem("otpCountdownData");
      } else {
        setSignupFormData(signupData);
        setShouldShowOtpForm(true);
      }
    }
  }, []);

  const renderFooter = () => {
    switch (tenantConfig.entity_short_name.toLowerCase()) {
      case "alta":
        return (
          <>
            If you have any issues with signing in, please reach out via our
            Helpdesk by clicking on the icon below or contact us at{" "}
            <PrimeLink href={"mailto:" + tenantConfig.email.generic}>
              {tenantConfig.email.generic}
            </PrimeLink>
            .
          </>
        );
      default:
        return (
          <>
            If you have any issues with signing in, contact us at{" "}
            <PrimeLink href={"mailto:" + tenantConfig.email.generic}>
              {tenantConfig.email.generic}
            </PrimeLink>
            .
          </>
        );
    }
  };

  if (isSignupSuccess) {
    return (
      <>
        <Head>
          <title>Signing In - {tenantConfig.entity_short_name}</title>
        </Head>
        <AuthLayout componentId="signup-auth" country={currentCountry}>
          <h1 className="font-light tracking-wide">
            Signing you into {tenantConfig.entity_short_name}
          </h1>
        </AuthLayout>
      </>
    );
  }

  return (
    <>
      <Head>
        <title>
          {props.title
            ? props.title
            : `Sign Up - ${tenantConfig.entity_short_name}`}
        </title>
      </Head>
      {featureFlag("ecf") && !isLoading && (
        <CountryNavbarComponent
          componentId="signup-navbar"
          countryCode={currentCountry}
          setCountry={setCountry}
        />
      )}
      <AuthLayout componentId="signup-auth" country={currentCountry}>
        {shouldShowOtpForm ? (
          <>
            <h1 className="font-light tracking-wide">
              Two-factor authentication
            </h1>
            <div id="signup-verify-otp-box">
              <h2>Check your email</h2>
              <p>
                <span>Please enter OTP sent to:</span>
                <br />
                <span
                  id="signup-auth-otp-text-email"
                  className="font-bold text-black"
                >
                  {signupFormData.email}
                </span>
              </p>
              <AuthOtpComponent
                useFormProps={useOtpFormProps}
                onSubmit={onSubmitOtp}
                isFetching={isFetching}
                componentId="signup-auth-otp-form"
              />
              <p className="pt-2 text-xs leading-normal">
                You will receive an email if an account exists.
                <br />
                <div className="mt-1"></div>
                {renderFooter()}
              </p>
            </div>
          </>
        ) : (
          <>
            <h1 className="font-light tracking-wide">Sign up for an account</h1>
            <div>
              <h2>Create an account</h2>
              {IS_RECAPTCHA_ENABLED && (
                <ReCAPTCHA
                  ref={recaptchaRef}
                  size="invisible"
                  sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ""}
                  badge="bottomleft"
                />
              )}
              <AuthSignupComponent
                info={info}
                isFetching={isFetching}
                onSubmit={onSubmitSignup}
                useFormProps={useSignupFormProps}
                tncs={tncs}
                currentCountry={currentCountry}
                componentId="signup-form"
              />
              <div
                id="signup-text-login"
                className="mx-auto text-sm !mt-4 w-fit"
              >
                Already have an account?{" "}
                <PrimeLink
                  href={
                    props.campaignCode === renew2022CampaignData.data.code
                      ? "/renew2022/login"
                      : "/login"
                  }
                  nextLink
                >
                  Sign in
                </PrimeLink>
              </div>
            </div>
          </>
        )}
      </AuthLayout>
    </>
  );
}

export default SignUpPage;
